import { assert } from 'torqcloud-ui'
import { ConfigOverrides } from './configOverrides'
import { resolveEnvironmentMarking } from './utils/environmentMarking'

export const apiConfig = {
  API_VERSIONS: {
    V1: 'v1',
  },
}

// Commercial/SaaS support address, used when neither SUPPORT_EMAIL nor VITE_SUPPORT_EMAIL is set.
// GCC supplies its own IT Ops address through its .env file or a mounted overrides.js.
const DEFAULT_SUPPORT_EMAIL = 'contact@support.torqcloud.io'

const DEFAULT_HELP_BASE_URL = 'https://help.buildworkforce.ai/en/'
const DEFAULT_HELP_ARTICLES = {
  userGuide: '',
  howToVideos: 'category/how-to-videos-1lft7w/',
  teammateBasics: 'article/building-your-first-teammate-1tlvj3l/',
  playgroundBasics: 'article/creating-teammates-in-playground-14yp0a4/',
  nativeTools: 'article/native-tools-1w8392r/',
  toolsArticles: 'category/tools-1ko6sz3/',
  understandingKnowledgeBase: 'article/understanding-knowledge-base-in-workforce-winkz2/',
  uploadingDocumentsToKnowledgeBase: 'article/uploading-documents-to-knowledge-base-byp8gs/',
  ragWithWorkforce: 'article/rag-with-workforce-b2qddp/',
}

// Base URL for every in-app help entry point: the "Get Help" buttons, the teammate
// editor's "Need Help?" links, and the article links below. Set it to an empty string
// to remove all of them (GCC, where these resolve to Crisp-hosted content).
const helpBaseUrl: string = ConfigOverrides.HELP_BASE_URL ?? import.meta.env.VITE_HELP_BASE_URL ?? DEFAULT_HELP_BASE_URL
// An empty base URL means "no help site", so the article paths go with it. Without this,
// the defaults would survive and concatenate to origin-relative dead links such as
// "article/native-tools-1w8392r/". Deployments that disable help then need to set only
// one value, and cannot half-disable it.
const helpArticles: Partial<typeof DEFAULT_HELP_ARTICLES> = helpBaseUrl ? ConfigOverrides.HELP_ARTICLES ?? DEFAULT_HELP_ARTICLES : {}

export const appConfig = {
  APP_NAME: import.meta.env.VITE_NAME,
  fontFamily: `'Roboto', sans-serif`,
  borderRadius: 12,
  // constant
  gridSpacing: 3,
  drawerWidth: 260,
  appDrawerWidth: 320,
  maxScroll: 100000,
  flowiseBaseUrl: ConfigOverrides.FLOWISE_API ?? import.meta.env.VITE_FLOWISE_API,
  chatBotHost: ConfigOverrides.CHATBOT_HOST ?? import.meta.env.VITE_CHATBOT_HOST,
  uiBaseURL: window.location.origin,
  redirectToWingman: ConfigOverrides.WINGMAN_URL ?? import.meta.env.VITE_WINGMAN_URL,
  allowedReturnOrigins:
    (ConfigOverrides.ALLOWED_RETURN_ORIGINS ?? import.meta.env.VITE_ALLOWED_RETURN_ORIGINS)?.split(',')?.map(u => new URL(u).origin) ?? [],
  torqCloudApiUrl: ConfigOverrides.TORQCLOUD_API_URL ?? import.meta.env.VITE_TORQCLOUD_API_URL,
  paymentPortalUrl: ConfigOverrides.PAYMENT_PORTAL_URL ?? import.meta.env.VITE_PAYMENT_PORTAL_URL,
  enableRegistration: (ConfigOverrides.ENABLE_REGISTRATION ?? import.meta.env.VITE_ENABLE_REGISTRATION) !== 'false',
  enableDomainBasedRegistration:
    (ConfigOverrides.ENABLE_DOMAIN_BASED_REGISTRATION ?? import.meta.env.VITE_ENABLE_DOMAIN_BASED_REGISTRATION) === 'true',
  cognitoAdminConfirmation: (ConfigOverrides.COGNITO_ADMIN_CONFIRMATION ?? import.meta.env.VITE_COGNITO_ADMIN_CONFIRMATION) === 'true',
  cognitoUseUuidUsername: (ConfigOverrides.COGNITO_USE_UUID_USERNAME ?? import.meta.env.VITE_COGNITO_USE_UUID_USERNAME) === 'true',
  // Show the current organization's license tier (organization_level) as "Your plan" and pre-fill
  // the profile's Company from the organization name. Off unless set to 'true'.
  showOrganizationLicense: (ConfigOverrides.SHOW_ORGANIZATION_LICENSE ?? import.meta.env.VITE_SHOW_ORGANIZATION_LICENSE) === 'true',
  environmentMarking: resolveEnvironmentMarking(ConfigOverrides.ENVIRONMENT_MARKING, import.meta.env.VITE_ENVIRONMENT_MARKING),
  supportEmail: ConfigOverrides.SUPPORT_EMAIL ?? import.meta.env.VITE_SUPPORT_EMAIL ?? DEFAULT_SUPPORT_EMAIL,
  sentryDsn: ConfigOverrides.SENTRY_DSN ?? import.meta.env.VITE_SENTRY_DSN,
  sentryEnvironment: ConfigOverrides.SENTRY_ENVIRONMENT ?? import.meta.env.VITE_ENV,
  uiVersion: ConfigOverrides.VERSION ?? 'unknown',
  helpBaseUrl,
  helpArticles,
  // Previously the User Guide link was derived from helpBaseUrl + helpArticles.userGuide,
  // which tied it to the same switch that hides every "Get Help" / "Need Help?" entry
  // point. GCC needs the opposite: this one link kept (its own SharePoint guide, a
  // different host) while all the others are suppressed. So it gets its own absolute
  // override, defaulting to that derivation so commercial behaviour is unchanged.
  userGuideUrl:
    ConfigOverrides.USER_GUIDE_URL ??
    import.meta.env.VITE_USER_GUIDE_URL ??
    (helpBaseUrl ? `${helpBaseUrl}${helpArticles.userGuide ?? ''}` : ''),
}

let _authenticationProvider: 'cognito' | 'keycloak' | 'oidc'
switch (ConfigOverrides.AUTHENTICATION_PROVIDER ?? import.meta.env.VITE_AUTHENTICATION_PROVIDER) {
  case 'cognito':
  case '':
  case undefined:
  case null:
    _authenticationProvider = 'cognito'
    assert(ConfigOverrides.COGNITO_REGION ?? import.meta.env.VITE_COGNITO_REGION, 'REACT_APP_COGNITO_REGION not set')
    assert(ConfigOverrides.COGNITO_USER_POOL_ID ?? import.meta.env.VITE_COGNITO_USER_POOL_ID, 'REACT_APP_COGNITO_USER_POOL_ID not set')
    assert(ConfigOverrides.COGNITO_APP_CLIENT_ID ?? import.meta.env.VITE_COGNITO_APP_CLIENT_ID, 'REACT_APP_COGNITO_APP_CLIENT_ID not set')
    assert(ConfigOverrides.COGNITO_COOKIE_DOMAIN ?? import.meta.env.VITE_COGNITO_COOKIE_DOMAIN, 'REACT_APP_COGNITO_COOKIE_DOMAIN not set')

    break
  case 'keycloak':
    _authenticationProvider = 'keycloak'
    assert(ConfigOverrides.KEYCLOAK_URL ?? import.meta.env.VITE_KEYCLOAK_URL, 'REACT_APP_KEYCLOAK_URL not set')
    assert(ConfigOverrides.KEYCLOAK_CLIENT_ID ?? import.meta.env.VITE_KEYCLOAK_CLIENT_ID, 'REACT_APP_KEYCLOAK_CLIENT_ID not set')
    assert(ConfigOverrides.TORQCLOUD_API_URL ?? import.meta.env.VITE_TORQCLOUD_API_URL, 'REACT_APP_TORQCLOUD_API_URL not set')
    break
  case 'oidc':
    _authenticationProvider = 'oidc'
    assert(ConfigOverrides.OIDC_AUTHORITY ?? import.meta.env.VITE_OIDC_AUTHORITY, 'REACT_APP_OIDC_AUTHORITY not set')
    assert(ConfigOverrides.OIDC_CLIENT_ID ?? import.meta.env.VITE_OIDC_CLIENT_ID, 'REACT_APP_OIDC_CLIENT_ID not set')

    break
  default:
    throw new Error(
      `Unrecognized Authentication Provider ${ConfigOverrides.AUTHENTICATION_PROVIDER ?? import.meta.env.VITE_AUTHENTICATION_PROVIDER}`
    )
}

export const authenticationProviderType = _authenticationProvider

export const awsConfig = {
  region: (ConfigOverrides.COGNITO_REGION ?? import.meta.env.VITE_COGNITO_REGION) as string,
  userPoolId: (ConfigOverrides.COGNITO_USER_POOL_ID ?? import.meta.env.VITE_COGNITO_USER_POOL_ID) as string,
  appClientId: (ConfigOverrides.COGNITO_APP_CLIENT_ID ?? import.meta.env.VITE_COGNITO_APP_CLIENT_ID) as string,
  cookieDomain: (ConfigOverrides.COGNITO_COOKIE_DOMAIN ?? import.meta.env.VITE_COGNITO_COOKIE_DOMAIN) as string,
}

export const keycloakConfig = {
  authority: (ConfigOverrides.KEYCLOAK_URL ?? import.meta.env.VITE_KEYCLOAK_URL) as string,
  client_id: (ConfigOverrides.KEYCLOAK_CLIENT_ID ?? import.meta.env.VITE_KEYCLOAK_CLIENT_ID) as string,
  torqCloudBaseUrl: (ConfigOverrides.TORQCLOUD_API_URL ?? import.meta.env.VITE_TORQCLOUD_API_URL) as string,
  cookieDomain: (ConfigOverrides.KEYCLOAK_COOKIE_DOMAIN ?? import.meta.env.VITE_KEYCLOAK_COOKIE_DOMAIN) as string | undefined,
}

export const oidcConfig = {
  authority: (ConfigOverrides.OIDC_AUTHORITY ?? import.meta.env.VITE_OIDC_AUTHORITY) as string,
  client_id: (ConfigOverrides.OIDC_CLIENT_ID ?? import.meta.env.VITE_OIDC_CLIENT_ID) as string,
  providerLabel: (ConfigOverrides.OIDC_PROVIDER_LABEL ?? import.meta.env.VITE_OIDC_PROVIDER_LABEL ?? 'Microsoft') as string,
  scope: (ConfigOverrides.OIDC_SCOPE ?? import.meta.env.VITE_OIDC_SCOPE ?? 'openid profile email offline_access') as string,
}

export const torqCloudConfig = {
  url: ConfigOverrides.TORQCLOUD_API_URL ?? import.meta.env.VITE_TORQCLOUD_API_URL,
}
